import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import {
    IconError,
    Flex,
    Spacer,
} from '@zillow/constellation';

import { PHONE_VERIFICATION_STEPS } from 'app/shared/constants/WizardStepsConstants';
import appLogger from 'app/shared/loggers/appLogger';
import MaxAttemptsError from 'app/shared/components/Wizard/PhoneVerificationWizard/components/MaxAttemptsError';
import numberUtils from 'app/shared/utils/numberUtils';
import PostingActions from 'app/shared/actions/PostingActions';
import PhoneVerificationBodyTemplate from '../PhoneVerificationBodyTemplate';

import {
    StyledCTALabel,
    StyledCTAWrapper,
    StyledEditButton,
    StyledErrorText,
    StyledFullWidthButton,
    StyledText,
} from '../../StyledComponents';
import { StyledSpacer } from './StyledComponents';

const propTypes = {
    contactPhone: PropTypes.string.isRequired,
    currentStepIndex: PropTypes.number.isRequired,
    incrementStep: PropTypes.func.isRequired,
    isNumberEditable: PropTypes.bool.isRequired,
    onRequestVerificationCode: PropTypes.func, // eslint-disable-line react/require-default-props
    saveVerificationType: PropTypes.func.isRequired,
};

const GetVerificationCode = ({
    contactPhone,
    currentStepIndex,
    incrementStep,
    isNumberEditable,
    onRequestVerificationCode,
    saveVerificationType,
}) => {
    const dispatch = useDispatch();
    const [error, setError] = useState(null);

    const requestVerificationCode = (type) => {
        const contactObj = {
            phone: contactPhone,
            contactType: type,
            verificationType: type,
        };

        if (onRequestVerificationCode) {
            onRequestVerificationCode();
        }

        dispatch(PostingActions.property.requestVerification(contactObj))
            .then((result) => {
                if (result.success) {
                    saveVerificationType(type);
                    incrementStep();
                } else if (result.error.message.includes('Contact already verified')) {
                    incrementStep(PHONE_VERIFICATION_STEPS.VERIFICATION_SUCCESS);
                } else {
                    setError(result.error.message);
                }
            })
            .catch((err) => {
                setError(err.message);
                appLogger.logError({
                    message: err.message,
                    context: 'requestVerificationCode',
                });
            });
    };

    const isButtonDisabled = () => error === 'Too many attempts';

    const renderErrorMessage = () => {
        if (error === 'Too many attempts') {
            return <MaxAttemptsError />;
        }
        return (
            <Spacer marginTop="sm">
                <Flex display="flex" alignItems="center" justifyContent="center">
                    <IconError fontColor="red600" size="sm" marginRight="sm" />
                    <StyledErrorText
                        as="div"
                        fontType="bodySmall"
                    >
                        {error}
                    </StyledErrorText>
                </Flex>
            </Spacer>
        );
    };

    return (
        <PhoneVerificationBodyTemplate
            heading={`Step ${currentStepIndex + 1} - Get verification code`}
            subheading={(
                <StyledSpacer>
                    <StyledText as="div">We&#39;ll send a one-time verification code to</StyledText>
                    <StyledText as="div">
                        {`${numberUtils.formatPhoneNumberInput(contactPhone)}. `}
                        {isNumberEditable && (
                            <StyledEditButton
                                onClick={() => incrementStep(PHONE_VERIFICATION_STEPS.ADD_PHONE_NUMBER)}
                            >
                                Edit number
                            </StyledEditButton>
                        )}
                    </StyledText>
                </StyledSpacer>
            )}
        >
            <StyledCTAWrapper>
                <StyledCTALabel>How would you like to receive the code?</StyledCTALabel>
            </StyledCTAWrapper>
            <StyledFullWidthButton
                buttonType="primary"
                disabled={isButtonDisabled()}
                onClick={() => requestVerificationCode('sms')}
            >
                Send text message
            </StyledFullWidthButton>
            <Spacer marginTop="xs" />
            <StyledFullWidthButton
                buttonType="primary"
                disabled={isButtonDisabled()}
                onClick={() => requestVerificationCode('phone')}
            >
                Call me
            </StyledFullWidthButton>
            {error && (
                <Spacer marginY="xs">
                    {renderErrorMessage()}
                </Spacer>
            )}
        </PhoneVerificationBodyTemplate>
    );
};

GetVerificationCode.propTypes = propTypes;

export default GetVerificationCode;
